import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, DisplayPosition, withCitrusTheme, ZIndex } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { ScrollableBasket } from '@features/scrollable-basket'
import type { Recipe } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { incrementTutorialViewed } from 'actions/tutorial'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { checkoutBasket } from 'routes/Menu/actions/menuCheckoutClick'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { useFlexRecipesWeb } from 'routes/Menu/hooks/useFlexRecipesWeb'
import { getShowBasketTooltip } from 'routes/Menu/selectors/basket'
import {
  getBasketDate,
  getBasketMenuUuid,
  getBasketOrderId,
  getBasketRecipePositions,
  getBasketSlotId,
} from 'selectors/basket'
import { getBrowserType } from 'selectors/browser'
import { createGetActionTypeIsPending } from 'selectors/status'

import { useCheckoutButton } from '../../hooks/useCheckoutButton'
import { ViewBasketModal } from '../ScrollableBasket/ViewBasketModal'

const Container = withCitrusTheme(Box, () => ({
  margin: '0 auto',
}))

const BlurredBackground = withCitrusTheme(Box, () => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(30px)',
}))

const useRemoveNotLoadedRecipesEffect = () => {
  const { basketRecipes } = useBasketRecipes()
  const { removeRecipe } = useBasket()

  const { request } = useMenuMeta()
  const basketDate = useSelector(getBasketDate)

  useEffect(() => {
    if (!request || !basketDate) return

    const isCurrentMenuLoaded = basketDate === request.deliveryDate
    if (!isCurrentMenuLoaded) {
      return
    }

    basketRecipes.forEach(({ coreId, recipe }) => {
      if (!recipe) {
        removeRecipe(coreId, undefined, undefined, {
          markUnsaved: false,
        })
      }
    })
  }, [removeRecipe, basketRecipes, request, basketDate])
}

export const BasketContainer = () => {
  const isLoading = useSelector(createGetActionTypeIsPending(actionTypes.MENU_FETCH_DATA))
  const { isCheckoutButtonDisabled, isCheckoutButtonPending } = useCheckoutButton()
  const { basketRecipes } = useBasketRecipes()
  const recipePositions = useSelector(getBasketRecipePositions)
  const slotId = useSelector(getBasketSlotId)
  const basketOrderId = useSelector(getBasketOrderId)
  const basketDate = useSelector(getBasketDate)
  const menuUuid = useSelector(getBasketMenuUuid)
  const isTooltipSeen = useSelector(getShowBasketTooltip)
  const [viewBasketModalOpen, setViewBasketModalOpen] = useState(false)

  const { recipeCount, getQuantitiesForRecipeId } = useBasket()

  const selectedRecipes = useMemo<Recipe[]>(() => {
    const recipes: Recipe[] = []

    basketRecipes.forEach(({ recipe }) => {
      if (recipe) {
        const quantity = getQuantitiesForRecipeId(recipe.id)
        for (let i = 0; i < quantity; i++) {
          recipes.push(recipe)
        }
      }
    })

    return recipes
  }, [basketRecipes, getQuantitiesForRecipeId])

  const sortedSelectedRecipes = useMemo(() => {
    if (!recipePositions?.size) {
      return selectedRecipes
    }

    // we have to use coreId because recipePositions are coreId, which is from BASKET_RECIPE_ADD action
    const recipesByCoreId = new Map(selectedRecipes.map((recipe) => [recipe.coreId, recipe]))

    // Build ordered recipes based on recipePosition
    const orderedRecipes: Recipe[] = recipePositions
      .map((coreId) => recipesByCoreId.get(coreId))
      .filter((r: Recipe | undefined): r is Recipe => Boolean(r))
      .toArray()

    // Count how many times each recipe has been placed
    const placedCounts = orderedRecipes.reduce<Record<string, number>>((acc, recipe) => {
      acc[recipe.coreId] = (acc[recipe.coreId] ?? 0) + 1

      return acc
    }, {})

    // Filter out recipes already accounted for in orderedRecipes
    const leftoverRecipes = selectedRecipes.filter(({ coreId }) => {
      if (placedCounts[coreId]) {
        placedCounts[coreId] -= 1

        return false
      }

      return true
    })

    return [...leftoverRecipes, ...orderedRecipes]
  }, [recipePositions, selectedRecipes])

  useRemoveNotLoadedRecipesEffect()

  const dispatch = useDispatch()
  const { pricing } = usePricing()
  const isFlexEnabled = useFlexRecipesWeb()
  const view = useSelector(getBrowserType)
  const section = trackingKeys.menu

  const handleCheckout = useCallback(
    (e) => {
      e.stopPropagation()
      if (!isCheckoutButtonPending) {
        datadogRum.addAction('basket_checkout_button_clicked', {
          slotId,
          basketDate,
          basketOrderId,
        })
        dispatch(checkoutBasket({ section, view, pricing, isFlexEnabled }))
      }
    },
    [
      dispatch,
      section,
      view,
      pricing,
      isFlexEnabled,
      isCheckoutButtonPending,
      slotId,
      basketDate,
      basketOrderId,
    ],
  )

  const handleRecipeClick = useCallback(
    (recipeUuid) => dispatch(showDetailRecipe(recipeUuid)),
    [dispatch],
  )

  const handleOpenBasket = useCallback(() => {
    datadogRum.addAction('open_basket', {
      menuId: menuUuid,
      deliveryDate: basketDate,
      basketRecipeCount: selectedRecipes.length,
    })
    setViewBasketModalOpen(true)
  }, [basketDate, menuUuid, selectedRecipes.length])

  // If there are no recipes in the basket, don't show the basket
  if (recipeCount <= 0 || isLoading) {
    return null
  }

  return (
    <BlurredBackground
      bottom="0"
      left="0"
      position={DisplayPosition.Fixed}
      width="100%"
      zIndex={ZIndex.Sticky}
    >
      <Container maxWidth="1600px">
        {viewBasketModalOpen && (
          <ViewBasketModal
            isCheckoutDisabled={isCheckoutButtonDisabled}
            isCheckoutPending={isCheckoutButtonPending}
            onCloseModal={() => setViewBasketModalOpen(false)}
            onCheckout={handleCheckout}
          />
        )}
        <ScrollableBasket
          isCheckoutDisabled={isCheckoutButtonDisabled}
          isLoading={isLoading}
          onClickViewBasket={handleOpenBasket}
          onRecipeClick={(recipeUuid) => handleRecipeClick(recipeUuid)}
          selectedRecipes={sortedSelectedRecipes}
          showTooltip={!isTooltipSeen}
          onCloseTooltip={() => dispatch(incrementTutorialViewed('basket-tooltip'))}
        />
      </Container>
    </BlurredBackground>
  )
}
